import { set } from '@ember/object';
import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type DeviceModel from 'garaje/models/device';
import type DeviceStatusModel from 'garaje/models/device-status';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';

interface DeviceDeletedPayload {
  external_id?: string;
  device_id?: string;
  id?: string;
  product?: string;
  location_id?: string | number;
  zone_id?: string | number;
}

// Devices are scoped either to a location (most products + visitor iPads) or to a
// zone (property/Connect iPads, whose location_id is null). The devices-service
// list endpoint takes whichever as a query filter.
export type DeviceStatusScope = 'location' | 'zone';

interface ScopeRequest {
  scopeId: string;
  product?: string;
  scope?: DeviceStatusScope;
}

interface ActiveScope {
  scopeId: string;
  product?: string;
  scope: DeviceStatusScope;
}

interface InflightEntry {
  promise: Promise<boolean>;
  // False while this entry is a follow-up still queued behind a predecessor (its
  // query will start later, so it post-dates any forced call that arrives before
  // then and those calls may join it); true once the query has actually been issued.
  queryStarted: boolean;
}

// Snapshot age at which ensureStatuses callers that poll scopes with no live MQTT
// coverage (Global Overview spans locations other than the subscribed one) should
// consider the cache stale and refetch.
export const STATUS_SNAPSHOT_MAX_AGE_MS = 60_000;
const STATUS_QUERY_LIMIT = 100;
const MAX_STATUS_PAGES = 10;

interface StatusBackedDeviceRemovalSnapshot {
  externalId: string;
  devicePayload: Record<string, unknown> | null;
  statusPayload: Record<string, unknown> | null;
}

export default class DeviceStatusService extends Service {
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked statusByDeviceId = new Map<string, DeviceStatusModel>();
  @tracked fetchError: Error | null = null;
  @tracked fetchErrorsByScopeKey = new Map<string, Error>();
  // A key's presence means this scope's snapshot MAY be incomplete because the
  // devices-service API returned more pages than the runaway guard permits.
  @tracked truncatedScopeKeys = new Set<string>();
  // Cache keys are scope-typed (`location:123` / `zone:123`) so a location and a zone
  // that happen to share a numeric id don't collide and serve each other's devices.
  // A key's presence means that scope's snapshot has been loaded at least once —
  // multiple scopes can be loaded simultaneously (e.g. Global Overview fans out
  // across many locations), unlike the single-scope cache this replaced.
  @tracked loadedScopeKeys = new Set<string>();

  #inflight = new Map<string, InflightEntry>();
  // Bumped by clearStatuses() so a fetch already in flight at clear time discards
  // its results instead of resurrecting stale data into the cleared cache.
  #generation = 0;
  #activeScope: ActiveScope | null = null;
  // Epoch ms of each scope's last successful snapshot, keyed like loadedScopeKeys.
  // TS-private (not #) so unit tests can backdate entries to exercise maxAgeMs.
  private loadedAtByScopeKey = new Map<string, number>();

  // Fetches (and merges in) a single scope's statuses. Never rejects — failures are
  // captured in fetchError so Promise.all-ing over many scopes (ensureStatusesForScopes)
  // can't have one bad scope reject the whole batch. Concurrent calls for the same
  // scope key share a single in-flight request — that guarantee holds only while
  // everything from the #inflight.get check through the #inflight.set registration
  // stays synchronous (no await first); the concurrent-dedupe unit test enforces this.
  //
  // `force: true` (refetchScope, activateScope) additionally guarantees the resolved
  // snapshot reflects server state from AFTER the call: a forced call that collides
  // with an already-issued request queues a follow-up fetch behind it instead of
  // joining a request that may predate the event (e.g. an MQTT reconnect) that
  // prompted the refresh. Forced calls arriving while that follow-up is still queued
  // join it, so a collision window costs at most one extra query.
  private fetchScope(
    scopeId: string,
    product?: string,
    scope: DeviceStatusScope = 'location',
    options: { force?: boolean } = {},
  ): Promise<boolean> {
    const key = `${scope}:${scopeId}`;

    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const existing = this.#inflight.get(key);
    // Ordinary calls join whatever is registered. Forced calls may only join a fetch
    // whose query hasn't been issued yet (a queued follow-up necessarily starts after
    // this moment); otherwise they queue exactly one follow-up behind it.
    if (existing && (!options.force || !existing.queryStarted)) {
      return existing.promise;
    }

    const entry: InflightEntry = { promise: Promise.resolve(true), queryStarted: !existing };
    const run = existing
      ? existing.promise.then(() => {
          entry.queryStarted = true;
          return this.#doFetch(scopeId, product, scope);
        })
      : this.#doFetch(scopeId, product, scope);
    entry.promise = run.finally(() => {
      // clearStatuses() may have replaced #inflight (and a newer fetch may have
      // re-registered this key) while we were in flight — only remove our own entry,
      // never a successor's, or the successor loses its dedupe registration.
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
      if (this.#inflight.get(key) === entry) {
        this.#inflight.delete(key);
      }
    });
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
    this.#inflight.set(key, entry);
    return entry.promise;
  }

  async #doFetch(scopeId: string, product?: string, scope: DeviceStatusScope = 'location'): Promise<boolean> {
    const key = `${scope}:${scopeId}`;
    const generation = this.#generation;
    const scopeParam = scope === 'zone' ? 'zone_id' : 'location_id';

    try {
      const baseQueryParams: Record<string, string | number> = {
        [scopeParam]: scopeId,
        limit: STATUS_QUERY_LIMIT,
        ...(product ? { product } : {}),
      };
      const statusSnapshot: DeviceStatusModel[] = [];
      const seenCursors = new Set<string>();
      let cursor: string | undefined;
      let hitPageCap = false;

      // A note on atomicity: each successful page's records are normalized into
      // Ember Data's identity map by store.query, so a later page failure cannot
      // undo attribute freshening on records already loaded — the same in-place
      // updates onConnectionStatusChanged applies continuously. What IS atomic is
      // everything this service owns: scope membership in statusByDeviceId,
      // loadedScopeKeys, and error state only change after the loop completes.
      for (let page = 0; page < MAX_STATUS_PAGES; page++) {
        const statuses = await this.store.query('device-status', {
          ...baseQueryParams,
          ...(cursor ? { cursor } : {}),
        });
        if (this.#generation !== generation) {
          // clearStatuses() ran while this fetch was in flight; discard every page.
          return false;
        }

        statusSnapshot.push(...statuses.slice());
        const nextCursor = (statuses as typeof statuses & { meta?: { cursor?: unknown } }).meta?.cursor;
        cursor = typeof nextCursor === 'string' && nextCursor ? nextCursor : undefined;
        if (!cursor) {
          break;
        }
        // A repeated cursor is server misbehavior (unlike the page cap, which is a
        // policy bound on legitimately huge scopes): looping on it would burn the
        // remaining pages re-fetching the same rows and then publish that fragment
        // as a complete snapshot, evicting the scope's real entries. Fail the whole
        // fetch instead — the catch path records the error and the prior snapshot
        // stays intact.
        if (seenCursors.has(cursor)) {
          throw new Error(`devices-service returned a repeated pagination cursor for ${key}`);
        }
        seenCursors.add(cursor);
        if (page === MAX_STATUS_PAGES - 1) {
          hitPageCap = true;
        }
      }

      const isActiveScopeFetch = this.#activeScope?.scopeId === scopeId && this.#activeScope?.scope === scope;
      const newMap = new Map(this.statusByDeviceId);
      for (const [externalId, status] of newMap) {
        const belongsToScope =
          scope === 'location'
            ? String(status.locationId) === String(scopeId)
            : String(status.zoneId) === String(scopeId);
        // Statuses pushed by onConnectionStatusChanged for devices we never
        // snapshotted carry no locationId/zoneId. Deltas only flow for the actively
        // subscribed scope, so when refreshing THAT scope treat unscoped partials as
        // belonging to it — otherwise a deletion missed during a broker outage would
        // leave a ghost entry the snapshot can never reconcile away.
        const isUnscopedPartial = !status.locationId && !status.zoneId;
        if (belongsToScope || (isActiveScopeFetch && isUnscopedPartial)) {
          newMap.delete(externalId);
        }
      }
      for (const status of statusSnapshot) {
        // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
        newMap.set(status.externalId, status);
      }
      this.statusByDeviceId = newMap;

      const newLoaded = new Set(this.loadedScopeKeys);
      newLoaded.add(key);
      this.loadedScopeKeys = newLoaded;
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      this.loadedAtByScopeKey.set(key, Date.now());

      const newFetchErrors = new Map(this.fetchErrorsByScopeKey);
      newFetchErrors.delete(key);
      this.fetchErrorsByScopeKey = newFetchErrors;

      const newTruncated = new Set(this.truncatedScopeKeys);
      if (hitPageCap) {
        newTruncated.add(key);
      } else {
        newTruncated.delete(key);
      }
      this.truncatedScopeKeys = newTruncated;

      this.fetchError = null;
      return true;
    } catch (error) {
      if (this.#generation !== generation) {
        return false;
      }
      const fetchError = error as Error;
      this.fetchError = fetchError;
      const newFetchErrors = new Map(this.fetchErrorsByScopeKey);
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newFetchErrors.set(key, fetchError);
      this.fetchErrorsByScopeKey = newFetchErrors;
      this.metrics.logMonitorEvent('DEVICES_SERVICE_FETCH_ERROR', {
        [scopeParam]: scopeId,
        error: fetchError.message,
      });
      return false;
    }
  }

  onConnectionStatusChanged(payload: {
    external_id: string;
    product: string;
    connection_status: 'CONNECTED' | 'DISCONNECTED';
    connection_status_updated_at?: string;
    telemetry?: Record<string, unknown>;
  }): void {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const existing = this.statusByDeviceId.get(payload.external_id);
    if (existing) {
      set(existing, 'connectionStatus', payload.connection_status);
      if (payload.connection_status_updated_at) {
        set(existing, 'connectionStatusUpdatedAt', new Date(payload.connection_status_updated_at));
      }
      if (payload.telemetry) {
        set(existing, 'telemetry', { ...existing.telemetry, ...payload.telemetry });
      }
    } else {
      const newMap = new Map(this.statusByDeviceId);
      const partial = this.store.push({
        data: {
          id: payload.external_id,
          type: 'device-status',
          attributes: {
            externalId: payload.external_id,
            product: payload.product,
            connectionStatus: payload.connection_status,
            connectionStatusUpdatedAt: payload.connection_status_updated_at,
            telemetry: payload.telemetry,
          },
        },
      }) as DeviceStatusModel;
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newMap.set(payload.external_id, partial);
      this.statusByDeviceId = newMap;
    }

    this.syncTelemetryToDevice(payload.external_id, payload.telemetry);
  }

  onDeviceDeleted(payload: DeviceDeletedPayload): void {
    if (payload.product !== 'visitors') {
      return;
    }

    const externalId = payload.external_id ?? payload.device_id ?? payload.id;
    if (!externalId) {
      return;
    }

    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const status = this.statusByDeviceId.get(externalId);
    if (payload.location_id && status?.locationId && String(status.locationId) !== String(payload.location_id)) {
      return;
    }
    if (payload.zone_id && status?.zoneId && String(status.zoneId) !== String(payload.zone_id)) {
      return;
    }

    this.removeStatusBackedDevice(externalId);
  }

  removeStatusBackedDevice(
    externalId: string,
    options: { locationId?: string | number | null; zoneId?: string | number | null; unloadDevice?: boolean } = {},
  ): StatusBackedDeviceRemovalSnapshot | null {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const status = this.statusByDeviceId.get(externalId);
    if (options.locationId && status?.locationId && String(status.locationId) !== String(options.locationId)) {
      return null;
    }
    if (options.zoneId && status?.zoneId && String(status.zoneId) !== String(options.zoneId)) {
      return null;
    }

    const device = this.store.peekRecord('device', externalId);
    const locationId = options.locationId ?? status?.locationId;
    const zoneId = options.zoneId ?? status?.zoneId;
    const snapshot: StatusBackedDeviceRemovalSnapshot = {
      externalId,
      devicePayload: device ? this.devicePayload(device, { locationId, zoneId }) : null,
      statusPayload: status ? this.statusPayload(status) : null,
    };

    status?.unloadRecord();

    const newMap = new Map(this.statusByDeviceId);
    newMap.delete(externalId);
    this.statusByDeviceId = newMap;

    // Callers with an in-flight save on the device (e.g. mid-destroyRecord) must pass
    // unloadDevice: false — unloading an in-flight record throws in Ember Data, and
    // destroyRecord unloads the record itself once the request succeeds.
    if (options.unloadDevice ?? true) {
      device?.unloadRecord();
    }

    return snapshot;
  }

  restoreStatusBackedDevice(snapshot: StatusBackedDeviceRemovalSnapshot | null): void {
    if (!snapshot) {
      return;
    }

    let restoredStatus: DeviceStatusModel | null = null;
    if (snapshot.statusPayload) {
      restoredStatus = this.store.push(snapshot.statusPayload) as DeviceStatusModel;
    }

    if (snapshot.devicePayload) {
      this.store.push(snapshot.devicePayload);
    }

    if (restoredStatus) {
      const newMap = new Map(this.statusByDeviceId);
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newMap.set(snapshot.externalId, restoredStatus);
      this.statusByDeviceId = newMap;
    }
  }

  getStatus(externalId: string): DeviceStatusModel | null {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    return this.statusByDeviceId.get(externalId) ?? null;
  }

  // Loads a scope's statuses if it hasn't been loaded yet (or if the loaded snapshot
  // is older than options.maxAgeMs). Safe to call repeatedly (e.g. from a route's
  // model hook on every transition) — a no-op while the cache is fresh. Callers whose
  // scope has live MQTT coverage (the active location/zone) can omit maxAgeMs; scopes
  // with no push channel (Global Overview's other locations) should pass one, since
  // for them a snapshot only decays after it's taken.
  async ensureStatuses(
    scopeId: string,
    product?: string,
    scope: DeviceStatusScope = 'location',
    options: { maxAgeMs?: number } = {},
  ): Promise<boolean> {
    const key = `${scope}:${scopeId}`;
    if (this.loadedScopeKeys.has(key)) {
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
      const loadedAt = this.loadedAtByScopeKey.get(key);
      const isFresh =
        options.maxAgeMs === undefined || (loadedAt !== undefined && Date.now() - loadedAt <= options.maxAgeMs);
      if (isFresh) {
        return true;
      }
    }
    return this.fetchScope(scopeId, product, scope);
  }

  // Fans ensureStatuses out across many scopes in parallel (e.g. Global Overview
  // pages, which span devices from up to a page's worth of distinct locations).
  async ensureStatusesForScopes(
    scopes: ScopeRequest[],
    options: { maxAgeMs?: number } = {},
  ): Promise<{ failedScopeIds: string[] }> {
    const results = await Promise.all(
      scopes.map((s) => this.ensureStatuses(s.scopeId, s.product, s.scope ?? 'location', options)),
    );
    return {
      failedScopeIds: scopes.filter((_scope, index) => !results[index]).map((scope) => scope.scopeId),
    };
  }

  // Forces a fresh fetch for a scope regardless of whether it's already loaded,
  // e.g. after pairing a new device or reconnecting to the realtime channel where
  // deltas may have been missed. Guaranteed to resolve with a snapshot taken after
  // this call — see fetchScope's forced-collision handling.
  async refetchScope(scopeId: string, product?: string, scope: DeviceStatusScope = 'location'): Promise<boolean> {
    return this.fetchScope(scopeId, product, scope, { force: true });
  }

  // Marks a scope as stale so the next ensureStatuses call re-fetches it.
  invalidateScope(scopeId: string, scope: DeviceStatusScope = 'location'): void {
    const key = `${scope}:${scopeId}`;
    this.loadedAtByScopeKey.delete(key);
    const newFetchErrors = new Map(this.fetchErrorsByScopeKey);
    newFetchErrors.delete(key);
    this.fetchErrorsByScopeKey = newFetchErrors;
    const newTruncated = new Set(this.truncatedScopeKeys);
    newTruncated.delete(key);
    this.truncatedScopeKeys = newTruncated;
    if (!this.loadedScopeKeys.has(key)) {
      return;
    }
    const newLoaded = new Set(this.loadedScopeKeys);
    newLoaded.delete(key);
    this.loadedScopeKeys = newLoaded;
  }

  // Tracks the scope tied to the current MQTT subscription lifecycle (current-location
  // / current-zone), so refetchActiveScope can re-sync it after a broker reconnect.
  // Always takes a fresh snapshot: while this scope was inactive its MQTT deltas were
  // not being delivered, so a previously-loaded cache entry (location A → B → back to
  // A) cannot be trusted.
  async activateScope(scopeId: string, product?: string, scope: DeviceStatusScope = 'location'): Promise<void> {
    this.#activeScope = { scopeId, product, scope };
    await this.refetchScope(scopeId, product, scope);
  }

  // Re-syncs the active scope's snapshot — used after an MQTT reconnect, since a
  // broker disconnect may have dropped connection-status deltas pushed via MQTT.
  async refetchActiveScope(): Promise<void> {
    if (!this.#activeScope) {
      return;
    }
    const { scopeId, product, scope } = this.#activeScope;
    await this.refetchScope(scopeId, product, scope);
  }

  deactivateScope(): void {
    this.#activeScope = null;
  }

  clearStatuses(): void {
    this.#generation++;
    this.statusByDeviceId = new Map();
    this.loadedScopeKeys = new Set();
    this.fetchErrorsByScopeKey = new Map();
    this.truncatedScopeKeys = new Set();
    this.loadedAtByScopeKey = new Map();
    this.#inflight = new Map();
  }

  private syncTelemetryToDevice(externalId: string, telemetry?: Record<string, unknown>): void {
    if (!telemetry) {
      return;
    }

    const existingDevice = this.store.peekRecord('device', externalId);
    if (!existingDevice) {
      return;
    }

    const attributes: Record<string, unknown> = { ...telemetry };
    if ('sleeping' in telemetry) {
      attributes['is-sleeping'] = telemetry['sleeping'] ?? false;
    }

    const normalized = this.store.normalize('device', {
      type: 'devices',
      id: externalId,
      attributes,
    });

    this.store.push(normalized);
  }

  private statusPayload(status: DeviceStatusModel): Record<string, unknown> {
    return {
      data: {
        id: status.id,
        type: 'device-status',
        attributes: {
          companyId: status.companyId,
          product: status.product,
          externalId: status.externalId,
          locationId: status.locationId,
          zoneId: status.zoneId,
          connectionStatus: status.connectionStatus,
          connectionStatusUpdatedAt: status.connectionStatusUpdatedAt,
          telemetry: status.telemetry,
          lastEnrolledAt: status.lastEnrolledAt,
        },
      },
    };
  }

  private devicePayload(
    device: DeviceModel,
    scope: { locationId?: string | number | null; zoneId?: string | number | null } = {},
  ): Record<string, unknown> {
    const relationships: Record<string, unknown> = {};
    if (scope.locationId) {
      relationships['location'] = { data: { type: 'location', id: String(scope.locationId) } };
    }
    if (scope.zoneId) {
      relationships['zone'] = { data: { type: 'zone', id: String(scope.zoneId) } };
    }
    return {
      data: {
        id: device.id,
        type: 'device',
        attributes: {
          userToken: device.userToken,
          isConnected: device.isConnected,
          grantExpiresAt: device.grantExpiresAt,
          osVersion: device.osVersion,
          maximumOsVersion: device.maximumOsVersion,
          appVersion: device.appVersion,
          currentIpadAppVersion: device.currentIpadAppVersion,
          ipAddress: device.ipAddress,
          wifiNetwork: device.wifiNetwork,
          printerStatus: device.printerStatus,
          failoverPrinterStatus: device.failoverPrinterStatus,
          lastHeartbeatAt: device.lastHeartbeatAt,
          isSleeping: device.isSleeping,
          deviceType: device.deviceType,
          name: device.name,
          alias: device.alias,
        },
        ...(Object.keys(relationships).length > 0 ? { relationships } : {}),
      },
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'device-status-service': DeviceStatusService;
  }
}
